/**
 * PIKA-780 — weekend-aware position and width maths for Timeline items.
 *
 * This module is deliberately free of app imports. `canvas_timeline_util.js`
 * cannot be unit tested because importing it drags in DataManager,
 * ComposeManager, ProjectGroup and a circular import of the canvas_timeline
 * component — which is why `canvas_timeline.test.ts` is `describe.skip` and why
 * the position maths had no coverage. Keeping the arithmetic here lets it be
 * tested directly, and lets any weekend indicator read one descriptor instead of
 * re-deriving the maths.
 *
 * Canvas dates are integer day indices where 0 is 1 Jan 2000, a Saturday. That
 * is the same origin `getOutOfWeekendPadding` assumes with its `(date - 2) % 7`,
 * and CANVAS_DATE_DAYS_OFFSET_FIRST_WEEK = 2 is the offset of the first Monday.
 */

/** Offset of the first Monday from canvas date 0. Mirrors CANVAS_DATE_DAYS_OFFSET_FIRST_WEEK. */
const FIRST_MONDAY_OFFSET = 2;

/** Index of Saturday once a date is normalised to 0 = Monday. */
const FIRST_WEEKEND_INDEX = 5;

/** Longest run of consecutive weekend days, used to bound the boundary scans. */
const MAX_CONSECUTIVE_WEEKEND_DAYS = 2;

/**
 * The width that suppresses a bar: `drawGroupItems` only paints when
 * `width >= 0`. An item with no visible day has no column to occupy, so it must
 * keep suppressing the bar — a width of 0 would paint a zero-width sliver on the
 * following Monday, a date the item does not have. Making such a task findable
 * is the weekend notice's job, not the bar's.
 */
export const NO_VISIBLE_DAYS_WIDTH = -1;

export type TimelineItemDates = {
	startDate: number;
	endDate: number;
};

export type WeekendGeometry = {
	/** Pixel offset of the item's first visible day from the timeline start. */
	x: number;
	/** Pixel width covering only the days the item actually occupies. Never negative. */
	width: number;
	/**
	 * False when no bar and no hit area should exist: the item has no visible day,
	 * or its width rounds below zero, which is how the original maths suppressed a
	 * bar. Both branches apply the same rule.
	 */
	isDrawable: boolean;
	/** The item's real start date falls on a weekend that is currently hidden. */
	hiddenStart: boolean;
	/** The item's real end date falls on a weekend that is currently hidden. */
	hiddenEnd: boolean;
	/** The item occupies hidden weekend days exclusively. */
	hiddenOnly: boolean;
	/** First day of the item that has a column, or null when it has none. */
	firstVisibleDate: number | null;
	/** Last day of the item that has a column, or null when it has none. */
	lastVisibleDate: number | null;
};

/** Normalise a canvas date to 0 = Monday … 6 = Sunday. Safe for dates before the origin. */
export const getDayOfWeekIndex = (date: number): number => (((Math.floor(date) - FIRST_MONDAY_OFFSET) % 7) + 7) % 7;

/** True when the given canvas date is a Saturday or a Sunday. */
export const isWeekendDate = (date: number): boolean => getDayOfWeekIndex(date) >= FIRST_WEEKEND_INDEX;

/**
 * Weekend days in the half-open range [from, to).
 *
 * Signed: when `to` precedes `from` the count of the reversed range is negated,
 * so callers can offset items that begin before the timeline start without a
 * separate "days until timeline" term.
 */
export const countWeekendDaysBetween = (from: number, to: number): number => {
	const start = Math.floor(from);
	const end = Math.floor(to);

	if (start === end) return 0;
	if (end < start) return -countWeekendDaysBetween(end, start);

	const totalDays = end - start;
	// Every whole week contributes exactly one Saturday and one Sunday.
	let weekendDays = Math.floor(totalDays / 7) * 2;

	const remainderStart = start + Math.floor(totalDays / 7) * 7;
	for (let date = remainderStart; date < end; date++) {
		if (isWeekendDate(date)) weekendDays++;
	}

	return weekendDays;
};

/**
 * Days that have a column between [from, to). With weekends hidden this is the
 * calendar distance minus the weekend days that were removed from the axis.
 */
export const countVisibleDaysBetween = (from: number, to: number, hideWeekends: boolean): number => {
	const calendarDays = Math.floor(to) - Math.floor(from);
	return hideWeekends ? calendarDays - countWeekendDaysBetween(from, to) : calendarDays;
};

/**
 * First day of the item that has a column, or null when every day is hidden.
 * Only meaningful with weekends hidden; with them shown every day has a column,
 * and getWeekendGeometry returns before reaching here.
 */
const findFirstVisibleDate = (item: TimelineItemDates): number | null => {
	const start = Math.floor(item.startDate);
	const end = Math.floor(item.endDate);

	for (let offset = 0; offset <= MAX_CONSECUTIVE_WEEKEND_DAYS; offset++) {
		const date = start + offset;
		if (date > end) return null;
		if (!isWeekendDate(date)) return date;
	}

	return null;
};

/**
 * The first date on or after `date` that has a column when weekends are hidden.
 * Saturday advances two days to Monday, Sunday one; a weekday is already visible.
 */
const getNextVisibleDate = (date: number): number => {
	const dayOfWeek = getDayOfWeekIndex(date);
	return dayOfWeek < FIRST_WEEKEND_INDEX ? date : date + (7 - dayOfWeek);
};

/** Last day of the item that has a column, or null when every day is hidden. See above. */
const findLastVisibleDate = (item: TimelineItemDates): number | null => {
	const start = Math.floor(item.startDate);
	const end = Math.floor(item.endDate);

	for (let offset = 0; offset <= MAX_CONSECUTIVE_WEEKEND_DAYS; offset++) {
		const date = end - offset;
		if (date < start) return null;
		if (!isWeekendDate(date)) return date;
	}

	return null;
};

/**
 * Everything the Timeline needs to draw an item, and everything a weekend
 * indicator needs to describe one.
 *
 * The bar is anchored to the item's first VISIBLE day rather than to its start
 * date. The old maths already landed on the same pixel — verified identical
 * across 1820 start/duration combinations — but only as a side effect of
 * `getWeekendDaysBefore` excluding the start day. Stating the anchor directly
 * keeps it true for the weekend-only case, where there is no visible day at all.
 *
 * The behaviour change is width: it counts visible days only and is clamped at
 * zero. A weekend-only task used to produce `-1`, so it was never painted and
 * could not be hovered, clicked or linked — the disappearance PIKA-780 is about.
 * `isDrawable` now carries that case explicitly instead of leaving it to a
 * negative number. `calculateItemWidth` still maps that case back to `-1`, so
 * the bar itself is unchanged; it is the notice that makes the task findable.
 *
 * `timelineStartDate` is NOT a whole day. The Timeline scrolls by sub-day
 * amounts (`this.startDate + deltaSeconds / SECONDS_PER_DAY`) and every other
 * date-to-pixel conversion in `canvas_timeline` keeps that fraction, so this one
 * must too — flooring it would park every bar on a whole-day boundary and make
 * it jump a full column at a time while the grid behind it scrolled smoothly.
 * The weekend counting still works off `Math.floor(timelineStartDate)`, exactly
 * as `getWeekendDaysBefore` did, and the fraction is subtracted afterwards.
 */
export const getWeekendGeometry = (
	item: TimelineItemDates,
	timelineStartDate: number,
	pixelsPerDay: number,
	hideWeekends: boolean
): WeekendGeometry => {
	const start = Math.floor(item.startDate);
	const end = Math.floor(item.endDate);
	const timelineStartFraction = timelineStartDate - Math.floor(timelineStartDate);

	if (!hideWeekends) {
		// Keep the original arithmetic exactly: unfloored dates, so a fractional
		// item date still yields a fractional width, and a negative result — which
		// an item with endDate < startDate produces — keeps suppressing the bar
		// through isDrawable, as the old -1 width did.
		const rawWidth = (item.endDate - item.startDate + 1) * pixelsPerDay - 1;

		return {
			x: (item.startDate - timelineStartDate) * pixelsPerDay,
			width: Math.max(0, rawWidth),
			isDrawable: rawWidth >= 0,
			hiddenStart: false,
			hiddenEnd: false,
			hiddenOnly: false,
			firstVisibleDate: start,
			lastVisibleDate: end,
		};
	}

	const firstVisibleDate = findFirstVisibleDate(item);
	const lastVisibleDate = findLastVisibleDate(item);
	const hiddenOnly = firstVisibleDate === null || lastVisibleDate === null;

	// With no visible day there is no bar. Anchor the descriptor on the next day
	// that does have a column so an indicator has somewhere to sit, but report
	// zero width and isDrawable false so nothing is painted on a date the item
	// does not occupy.
	const anchorDate = hiddenOnly ? getNextVisibleDate(start) : (firstVisibleDate as number);

	const visibleDays = hiddenOnly
		? 0
		: countVisibleDaysBetween(firstVisibleDate as number, (lastVisibleDate as number) + 1, true);
	// Same rule as the weekends-shown branch: a width the old maths made negative
	// -- one visible day at a zoom of under a pixel per day -- suppressed the bar,
	// and must keep doing so rather than painting a zero-width one.
	const rawWidth = visibleDays * pixelsPerDay - 1;

	return {
		x: (countVisibleDaysBetween(Math.floor(timelineStartDate), anchorDate, true) - timelineStartFraction) * pixelsPerDay,
		width: Math.max(0, rawWidth),
		isDrawable: !hiddenOnly && rawWidth >= 0,
		hiddenStart: isWeekendDate(start),
		hiddenEnd: isWeekendDate(end),
		hiddenOnly,
		firstVisibleDate,
		lastVisibleDate,
	};
};

/** An item whose real dates reach into a weekend the Timeline is hiding. */
export type WeekendDatedItem<T> = {
	item: T;
	/** Real start date, whatever the bar shows. */
	startDate: number;
	/** Real end date, whatever the bar shows. */
	endDate: number;
	hiddenStart: boolean;
	hiddenEnd: boolean;
	/** No visible day at all, so no bar is drawn for it. */
	hiddenOnly: boolean;
};

/**
 * The items a weekend indicator needs to name, in timeline order.
 *
 * An item qualifies when one of its own boundary dates sits on a hidden weekend
 * — that is the date the user cannot see. An item that merely *spans* a weekend,
 * Friday to Monday, has both boundaries visible and is deliberately excluded:
 * the ticket requires those to behave exactly as they do today.
 *
 * Returns nothing at all when weekends are shown, so callers need no second guard.
 */
export const collectWeekendDatedItems = <T extends TimelineItemDates>(
	items: readonly T[],
	hideWeekends: boolean
): WeekendDatedItem<T>[] => {
	if (!hideWeekends) return [];

	return items
		.filter(item => isWeekendDate(item.startDate) || isWeekendDate(item.endDate))
		.map(item => {
			const startDate = Math.floor(item.startDate);
			const endDate = Math.floor(item.endDate);

			return {
				item,
				startDate,
				endDate,
				hiddenStart: isWeekendDate(startDate),
				hiddenEnd: isWeekendDate(endDate),
				// No first visible day means no last one either, so one scan settles it.
				hiddenOnly: findFirstVisibleDate(item) === null,
			};
		})
		.sort((a, b) => a.startDate - b.startDate || a.endDate - b.endDate);
};

/**
 * Weekend days crossed by a move of `movedDaysWithoutWeekend` working days that
 * starts at `canvasTimelineDate`.
 *
 * Moved here from `canvas_timeline_util.js` so it can be tested; the arithmetic
 * is unchanged, and `moveItem` still owns the alignment with `ShiftUtil.java`.
 *
 * A weekend date has no working-day index of its own, so it is clamped onto the
 * adjacent working day — a forward move reads it as the following Monday, a
 * backward move as the preceding Friday. The clamp makes the COUNT right; it
 * does not move the date. `moveItem`'s `getOutOfWeekendPadding` is the only
 * thing that compensates for it, which is why a weekend boundary must never be
 * handed to this directly: one working day forward from a Saturday counts no
 * weekend at all and lands on the Sunday (PIKA-826).
 */
export const getWeekendDaysDuringDiff = (canvasTimelineDate: number, movedDaysWithoutWeekend: number): number => {
	const date = Math.floor(canvasTimelineDate);
	const dayOfWeek = movedDaysWithoutWeekend > 0 ? Math.max(date % 7, 2) - 2 : Math.min((date - 2) % 7, 4);
	return Math.floor((dayOfWeek + movedDaysWithoutWeekend) / 5) * 2;
};

/** `movedDays` expressed in calendar days: with weekends hidden, the ones it skips are added back. */
export const weekendAdjustedMovedDays = (canvasDate: number, movedDays: number, hideWeekend: boolean): number =>
	hideWeekend ? movedDays + getWeekendDaysDuringDiff(canvasDate, movedDays) : movedDays;

/** Which edge of an item's bar a resize gesture is dragging. */
export type ResizeEdge = 'start' | 'end';

/**
 * The date a resized edge should take, or null to leave the stored date alone.
 *
 * The edge is resolved from the day the user can actually see. With weekends
 * hidden, an item's boundary may sit on a date that has no column — a task
 * dated Thu 22 to Sat 24 is drawn Thu 22 to Fri 23. Moving the stored Saturday
 * by one working day used to land on Sunday: still hidden, so the bar did not
 * move while the deadline did. Anchoring on the last visible day instead
 * (Fri 23) sends the edge to Mon 26, which is the column the user dragged to.
 *
 * Null is returned in the two cases where no honest date can be written:
 *
 *  - the item has no visible day at all, so there is no bar to grab;
 *  - the gesture cannot change the bar. A one-column bar dragged inward can
 *    only shuffle hidden days around, and moving the stored date for that would
 *    be the same silent change in a new place.
 *
 * With weekends shown every date has a column, so the anchor is the stored
 * boundary and every result is what the resize branch has always produced.
 */
export const resolveResizedEdge = (
	item: TimelineItemDates,
	edge: ResizeEdge,
	movedDays: number,
	hideWeekends: boolean
): number | null => {
	const start = Math.floor(item.startDate);
	const end = Math.floor(item.endDate);
	const isEndEdge = edge === 'end';

	const firstVisibleDate = hideWeekends ? findFirstVisibleDate(item) : start;
	const lastVisibleDate = hideWeekends ? findLastVisibleDate(item) : end;

	// No visible day means no bar and no hit area, so there is no edge to drag.
	if (firstVisibleDate === null || lastVisibleDate === null) return null;

	const anchor = isEndEdge ? lastVisibleDate : firstVisibleDate;
	const moved = anchor + weekendAdjustedMovedDays(anchor, movedDays, hideWeekends);

	// Stop the item inverting, as the resize branch always has. The bound is the
	// opposite VISIBLE edge rather than the stored boundary, so an edge dragged
	// all the way in cannot come to rest on a hidden day either.
	const resized = isEndEdge ? Math.max(moved, firstVisibleDate) : Math.min(moved, lastVisibleDate);

	if (!hideWeekends) return resized;

	const next = isEndEdge ? {startDate: start, endDate: resized} : {startDate: resized, endDate: end};
	const barWouldMove = findFirstVisibleDate(next) !== firstVisibleDate || findLastVisibleDate(next) !== lastVisibleDate;

	return barWouldMove ? resized : null;
};

/**
 * True when a completed gesture moved a boundary off a weekend the Timeline is
 * hiding.
 *
 * `moveItem` snaps a weekend boundary onto a working day and writes that snap to
 * the stored date, so a drag can change a date the user never saw and cannot
 * see afterwards. The snap itself is intended — the Timeline works in working
 * days — but it has to be announced, which is what this gates (PIKA-826).
 *
 * A boundary that lands on another weekend day has not been snapped off the
 * weekend, so it is not reported.
 */
export const hasSnappedOffHiddenWeekend = (
	before: TimelineItemDates,
	after: TimelineItemDates,
	hideWeekends: boolean
): boolean => {
	if (!hideWeekends) return false;

	return (
		(isWeekendDate(before.startDate) && !isWeekendDate(after.startDate)) ||
		(isWeekendDate(before.endDate) && !isWeekendDate(after.endDate))
	);
};

/** The dates a resize writes, plus the deltas its callers report to `onMoving`. */
export type ResizedItemDates = {
	startDate: number;
	endDate: number;
	startDifference: number;
	endDifference: number;
};

/**
 * Everything a resize gesture assigns, for one evaluation of that gesture.
 *
 * `initial` is the item as the gesture found it and `totalMovedDays` the whole
 * displacement since; `current` is the item as earlier evaluations left it.
 * Resolving from `initial` rather than `current` is what makes the result depend
 * only on where the cursor is, never on the route it took -- see
 * `resolveResizedEdge`. That is also why a null resolution restores the
 * boundary from `initial`: `current` may still carry a value an earlier
 * evaluation wrote, and leaving it there would strand the item on a date the
 * user has since dragged away from.
 *
 * The differences are measured against `current`, because that is the state the
 * caller's listeners are holding.
 */
export const resolveResizedDates = (
	initial: TimelineItemDates,
	current: TimelineItemDates,
	edge: ResizeEdge,
	totalMovedDays: number,
	hideWeekends: boolean
): ResizedItemDates => {
	const resized = resolveResizedEdge(initial, edge, totalMovedDays, hideWeekends);
	const isEndEdge = edge === 'end';
	const moved = resized === null ? (isEndEdge ? initial.endDate : initial.startDate) : resized;

	const startDate = isEndEdge ? current.startDate : moved;
	const endDate = isEndEdge ? moved : current.endDate;

	return {
		startDate,
		endDate,
		startDifference: startDate - current.startDate,
		endDifference: endDate - current.endDate,
	};
};

/**
 * Whether a gesture left the item on different dates than it found it on.
 *
 * The Timeline gates its commit on dayDifference, which counts the columns the
 * cursor crossed -- not movement of the item. A resize that cannot change the
 * bar resolves back to the dates it started from, and committing that sends an
 * update carrying values already stored, which reads as a change nobody made.
 *
 * A gesture that recorded no starting dates counts as moved, so it commits
 * exactly as it did before this gate existed. Only meaningful where the group
 * cannot change, since a group move must commit whatever the dates did.
 */
export const hasMovedFromGestureStart = (
	gestureStart: {startDate?: number; endDate?: number},
	item: TimelineItemDates
): boolean => {
	const {startDate, endDate} = gestureStart;

	if (startDate === undefined || endDate === undefined) return true;

	return item.startDate !== startDate || item.endDate !== endDate;
};

/**
 * How far the Timeline scrolled during a gesture, in the units a drag measures:
 * columns, not calendar days.
 *
 * Signed, and half-open through countWeekendDaysBetween, so a view that never
 * moved reports nothing. The Timeline scrolls by fractions of a day and the
 * drag maths depends on that precision, so only the weekend count floors --
 * the distance itself keeps its fraction.
 *
 * PIKA-826 F1: the previous correction counted both endpoints, so an unmoved
 * Timeline whose left edge happened to be a Saturday or Sunday still reported a
 * day of scroll, and every gesture landed a column past where it was dropped.
 * The per-user Weekends toggle leaves the left edge on a weekend until the page
 * is reloaded, which is where it showed.
 */
export const getScrolledVisibleDays = (fromDate: number, toDate: number, hideWeekends: boolean): number =>
	toDate - fromDate - (hideWeekends ? countWeekendDaysBetween(fromDate, toDate) : 0);
